import React, { useCallback, memo, useState, useEffect } from 'react';
import type { RemirrorJSON } from 'remirror';
import { ProjectFragment } from 'components/fragments.generated';
import {
  ProjectAccessAction,
  ProjectAccessSubject,
} from '../../../server/abilities/ProjectAccessEnums';
import { ActorVariant } from '../../../server/graphql/__generated.types';
import { ClipObject, ClipObjectPending } from '../../context/clips';
import {
  EventName,
  useEventsContext,
} from '../../context/events/EventsContext';
import {
  Can,
  useProjectAccessAbility,
} from '../../context/projectAccess/ProjectAccessContext';
import StudioClipActionDelete from '../StudioClip/Actions/StudioClipActionDelete';
import StudioClipActionDownload from '../StudioClip/Actions/StudioClipActionDownload';
import StudioClipActionRegenerate, {
  RegenerateType,
} from '../StudioClip/Actions/StudioClipActionRegenerate';
import StudioClipActions from '../StudioClip/Actions/StudioClipActions';
import StudioClip, { StudioClipProps } from '../StudioClip/StudioClip';
import { getCoreEditorRef } from '../../context/editorV2/CoreEditorContext';
import { usePromptsContext } from '../../context/prompts/PromptsContext';
import {
  Actions,
  Subjects,
  useUserContext,
  useUserAbilitiesContext,
} from '../../context/user/UserContext';
import { ClipPreview } from '../../context/previewClip/StudioPreviewClipContext';
import { useAppDispatch, useAppSelector } from '../../store/hooks';

import StudioClipActionComment from '../StudioClip/Actions/StudioClipActionComment';
import { hideCommentsSidebar } from '../../store/CommentsSlice';
import {
  LayoutElementState,
  useLayoutContext,
} from '../BaseLayout/context/LayoutContext';
import useIsLegacyProjectViewOnly from '../Studio/legacy/useIsLegacyProjectViewOnly';

const StudioClipWithUserActions = ({
  clip,
  previewClip,
  onDelete,
  onRename,
  onReplaceEditorValue,
  selected,
  onSelectedChange,
  setActiveActor,
  studioClipCompleteClasses,
  draggableProvided,
  isDragging,
  hideClipBodyTop,
  hideBorder,
  handleSave,
  setDownloadError,
  project,
}: StudioClipWithUserActionsProps) => {
  const ACTOR_NOT_FOUND_ID = 404;

  const coreEditorRef = getCoreEditorRef();
  const [eventTracking] = useEventsContext();
  const projectAccessAbility = useProjectAccessAbility();
  const userAbility = useUserAbilitiesContext();
  const { data: userQuery } = useUserContext();
  const [, promptsDispatch] = usePromptsContext();
  const commentsState = useAppSelector(appState => appState.comments);
  const appDispatch = useAppDispatch();
  const { rightRailVisibility } = useLayoutContext();
  const isLegacyViewOnly = useIsLegacyProjectViewOnly(project.studioVersion);

  const handleDelete = useCallback(() => {
    // Handle clip deletion
    if (!previewClip) {
      if (!userQuery?.user.settings.doNotShowClipDeletionConfirmationModal) {
        promptsDispatch({
          type: 'OpenBatchDeleteClipsPrompt',
          payload: {
            clips: [clip as ClipObject],
            clipIds: [clip.id],
            onSuccess: () => {
              if (
                rightRailVisibility === LayoutElementState.OPEN &&
                commentsState.showSidebar &&
                commentsState.clipId === clip.id
              )
                appDispatch(hideCommentsSidebar());
            },
          },
        });
      } else {
        onDelete(clip as ClipObject).then(() => {
          if (
            rightRailVisibility === LayoutElementState.OPEN &&
            commentsState.showSidebar &&
            commentsState.clipId === clip.id
          )
            appDispatch(hideCommentsSidebar());
        });
      }

      return;
    }

    // Handle preview deletion
    if (previewClip) {
      if (
        !userQuery?.user.settings.doNotShowClipPreviewDeletionConfirmationModal
      ) {
        promptsDispatch({
          type: 'OpenDeleteClipPreviewPrompt',
          payload: {
            onDelete: () => {
              onDelete(previewClip);
            },
          },
        });
      } else {
        onDelete(previewClip);
      }
    }
  }, [
    previewClip,
    userQuery?.user.settings.doNotShowClipDeletionConfirmationModal,
    userQuery?.user.settings.doNotShowClipPreviewDeletionConfirmationModal,
    promptsDispatch,
    clip,
    rightRailVisibility,
    commentsState.showSidebar,
    commentsState.clipId,
    appDispatch,
    onDelete,
  ]);

  const handleNameChange = useCallback(
    (newName: string) => {
      onRename && onRename(clip as ClipObject, newName);
    },
    [clip, onRename]
  );

  const handleRegenerate = useCallback(() => {
    if (onReplaceEditorValue) {
      const value =
        (previewClip?.json as RemirrorJSON) ||
        (clip.textFormat as string) ||
        (clip.text as string);
      if (value) {
        onReplaceEditorValue(value);
      }
      // we do not update the active actor if regenerating combined clip
      if (!clip.combinedFrom && setActiveActor) {
        setActiveActor(
          clip.actor,
          clip.actor.variant,
          clip.ttsVersion ?? undefined
        );
      }
    }
    // Focus the editor after regenerating
    coreEditorRef?.commands.focus();

    const baseEventData = {
      clipId: clip.id,
      actorId: `${clip.actorId}`,
      actorVariantId: `${clip.actor.variant.id}`,
      actorVariantType: `${clip.actor.variant.variantType.id}`,
      actorName: clip.actor.name,
      textLength: clip.text.length,
      ttsVersion: String(clip.ttsVersion),
    };
    if (previewClip) {
      eventTracking.track({
        type: EventName.ClipPreviewRepopulateCues,
        data: baseEventData,
      });
    } else {
      eventTracking.track({
        type: EventName.ClipReRender,
        data: {
          ...baseEventData,
          isCombined: Boolean(clip.combinedFrom),
        },
      });
    }
  }, [
    onReplaceEditorValue,
    coreEditorRef?.commands,
    clip.id,
    clip.actorId,
    clip.actor,
    clip.text,
    clip.ttsVersion,
    clip.textFormat,
    clip.combinedFrom,
    previewClip,
    setActiveActor,
    eventTracking,
  ]);

  // I've done this 'hack' before, but it does seems to help a lot here.
  // NOTE: following hack causes `react-beautiful-dnd` to throw errors due to missing drag handle
  // so we add that to an empty div for initial render.
  const [isReady, setIsReady] = useState(false);
  useEffect(() => {
    setIsReady(true);
  }, []);
  if (!isReady) return <div {...draggableProvided?.dragHandleProps} />;

  const canEditAProject = projectAccessAbility.can(
    ProjectAccessAction.Edit,
    ProjectAccessSubject.Project
  );
  const canRenameClip = canEditAProject && !isLegacyViewOnly;
  // Legacy view-only keeps selection/download/delete; collaboration ReadOnly hides checkboxes.
  const isReadOnly = !canEditAProject && !isLegacyViewOnly;

  const canShowComments =
    userAbility.can(Actions.UseFeature, Subjects.Comments) ||
    userAbility.can(Actions.Read, Subjects.Comments);

  return (
    <StudioClip
      clip={clip}
      previewClip={previewClip}
      onNameChange={canRenameClip ? handleNameChange : undefined}
      selected={selected}
      onSelectedChange={onSelectedChange}
      studioClipCompleteClasses={studioClipCompleteClasses}
      draggableProvided={draggableProvided}
      isDragging={isDragging}
      isReadOnly={isReadOnly}
      hideClipBodyTop={hideClipBodyTop}
      hideBorder={hideBorder}
      handleSave={handleSave}
      playbackIconFilled
    >
      <StudioClipActions>
        {!hideClipBodyTop && canShowComments && !isLegacyViewOnly && (
          <StudioClipActionComment project={project} />
        )}
        {!isLegacyViewOnly && (
          <Can I={ProjectAccessAction.Edit} a={ProjectAccessSubject.Project}>
            {/* this MUST be clip.actor.id so we catch if GraphQL errors out while resolving the actor; clip.actorId will not be 404, clip.actorId is that actorId that was used to generate the clip */}
            {clip.actor.id !== ACTOR_NOT_FOUND_ID && (
              <StudioClipActionRegenerate
                regenerateType={
                  previewClip ? RegenerateType.cues : RegenerateType.script
                }
                onClick={handleRegenerate}
                className={
                  selected
                    ? studioClipCompleteClasses.classes.clipSelected
                    : studioClipCompleteClasses.classes.clipDeselected
                }
                project={project}
              />
            )}
          </Can>
        )}

        {!hideClipBodyTop && (
          <Can I={ProjectAccessAction.Download} a={ProjectAccessSubject.Clip}>
            <StudioClipActionDownload
              className={
                selected
                  ? studioClipCompleteClasses.classes.clipSelected
                  : studioClipCompleteClasses.classes.clipDeselected
              }
              setDownloadError={setDownloadError}
              project={project}
            />
          </Can>
        )}

        <Can I={ProjectAccessAction.Edit} a={ProjectAccessSubject.Project}>
          <StudioClipActionDelete
            aria-haspopup={'dialog'}
            onClick={handleDelete}
            className={
              selected
                ? studioClipCompleteClasses.classes.clipSelected
                : studioClipCompleteClasses.classes.clipDeselected
            }
            project={project}
          />
        </Can>
      </StudioClipActions>
    </StudioClip>
  );
};

export interface StudioClipWithUserActionsProps {
  clip: ClipObject | ClipObjectPending;
  previewClip?: ClipPreview;
  onDelete: (clip: ClipObject) => Promise<void>;
  onRename?: (clip: ClipObject, newName: string) => void;
  onReplaceEditorValue?: (value: any) => void;
  selected: boolean;
  project: ProjectFragment;
  onSelectedChange?: (
    clipId: string,
    selected: boolean,
    opts?: { isMultiSelect?: boolean }
  ) => void;
  setActiveActor?: (
    actor: Pick<ClipObject['actor'], 'id' | 'variant'>,
    variant?: ActorVariant,
    ttsVersion?: string
  ) => void;
  studioClipCompleteClasses: StudioClipProps['studioClipCompleteClasses'];
  draggableProvided?: StudioClipProps['draggableProvided'];
  isDragging?: StudioClipProps['isDragging'];
  projectId: string;
  hideClipBodyTop?: boolean;
  hideBorder?: boolean;
  handleSave?: ({
    clip,
    editorText,
    setIsClipSaved,
  }: {
    clip: ClipObject;
    editorText: string;
    setIsClipSaved: (isSaved: boolean) => void;
  }) => void;
  setDownloadError?: (downloadError: string) => void;
}

// onSelectedChange is problematic since it's an inline function,
// this prevented a heavy re-render
export default memo(
  StudioClipWithUserActions,
  (
    { clip: oldClip, selected: oldSelected, onSelectedChange },
    {
      clip: newClip,
      selected: newSelected,
      onSelectedChange: onSelectedChangeNew,
    }
  ) =>
    oldClip === newClip &&
    oldSelected === newSelected &&
    onSelectedChange === onSelectedChangeNew
);
